/**
 * Codec Groups Hooks - React Query hooks for SIP Codec Groups
 * 
 * Provides dropdown-compatible hooks for selecting codec groups
 * in forms like the SIP peer editor
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * Codec Group type for dropdown selection
 */
export interface CodecGroupOption {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  isDefault: boolean;
  codecs?: Array<{
    codecId: string;
    codecName: string;
    codecType: 'audio' | 'video' | 'fax';
    priority: number;
    enabled: boolean;
  }>;
}

interface CodecGroupsResponse {
  success: boolean;
  data: CodecGroupOption[];
  timestamp: string;
}

interface CodecGroupResponse {
  success: boolean;
  data: CodecGroupOption;
  timestamp: string;
}

/**
 * Fetch all codec groups - useful for dropdown selectors
 */
export function useCodecGroups(params?: {
  enabled?: boolean;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['codec-groups', params],
    queryFn: async (): Promise<CodecGroupsResponse> => {
      const response = await api.getCodecGroups(params);
      return {
        success: true,
        data: response.data || [],
        timestamp: response.timestamp
      };
    },
    staleTime: 60000, // Consider data fresh for 1 minute
  });
}

/**
 * Fetch codec groups formatted for dropdown selection
 * Returns only enabled groups with id and name
 */
export function useCodecGroupsForDropdown() {
  return useQuery({
    queryKey: ['codec-groups-dropdown'],
    queryFn: async () => {
      const response = await api.getCodecGroups({ enabled: true });
      const groups = response.data || [];
      return groups.map((group: { id: string; name: string; description?: string; isDefault?: boolean }) => ({
        value: group.id,
        label: group.name,
        description: group.description,
        isDefault: group.isDefault,
      }));
    },
    staleTime: 60000,
  });
}

/**
 * Fetch the default codec group
 */
export function useDefaultCodecGroup() {
  return useQuery({
    queryKey: ['codec-group-default'],
    queryFn: async () => {
      const response = await api.getDefaultCodecGroup();
      return response.data;
    },
    staleTime: 60000,
  });
}

/**
 * Fetch single codec group by ID
 */
export function useCodecGroup(groupId: string | null | undefined) {
  return useQuery<CodecGroupResponse | null>({
    queryKey: ['codec-group', groupId],
    queryFn: async () => {
      if (!groupId) return null;
      const response = await api.getCodecGroupById(groupId);
      return {
        success: true,
        data: response.data,
        timestamp: response.timestamp
      };
    },
    enabled: !!groupId,
  });
}

/**
 * Create codec group mutation
 */
export function useCreateCodecGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<CodecGroupOption>) => {
      const response = await api.createCodecGroup(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codec-groups'] });
      queryClient.invalidateQueries({ queryKey: ['codec-groups-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['codec-group-default'] });
    },
  });
}

/**
 * Update codec group mutation
 */
export function useUpdateCodecGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, data }: { groupId: string; data: Partial<CodecGroupOption> }) => {
      const response = await api.updateCodecGroup(groupId, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['codec-groups'] });
      queryClient.invalidateQueries({ queryKey: ['codec-groups-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['codec-group', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['codec-group-default'] });
    },
  });
}

/**
 * Delete codec group mutation
 */
export function useDeleteCodecGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      await api.deleteCodecGroup(groupId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codec-groups'] });
      queryClient.invalidateQueries({ queryKey: ['codec-groups-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['codec-group-default'] });
    },
  });
}
